import {
  createSizedImage,
  drawImageToPng,
  loadImage,
} from './rasterizeClipboardImage';

const SVG_NS = 'http://www.w3.org/2000/svg';

const SVG_PAINT_PROPERTIES = [
  'fill',
  'stroke',
  'stop-color',
  'color',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
] as const;

const SVG_TEXT_PROPERTIES = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline',
  'alignment-baseline',
  'text-transform',
] as const;

const applyComputedSvgPaints = (source: Element, target: SVGElement) => {
  const computed = window.getComputedStyle(source);
  SVG_PAINT_PROPERTIES.forEach(property => {
    const value = computed.getPropertyValue(property);
    if (!value || value.includes('url(')) {
      return;
    }
    target.style.setProperty(property, value);
    if (property !== 'color' && property !== 'opacity') {
      target.setAttribute(property, value);
    }
  });
};

const applyComputedSvgTypography = (source: Element, target: SVGElement) => {
  const computed = window.getComputedStyle(source);
  SVG_TEXT_PROPERTIES.forEach(property => {
    const value = computed.getPropertyValue(property);
    if (!value) {
      return;
    }
    target.style.setProperty(property, value);
  });
};

// Isolated SVG data-URLs don't see the document's stylesheets, so faces
// must be embedded or labels fall back to Times.
const embedFontCssInSvg = (svg: SVGElement, fontCss: string) => {
  if (!fontCss || svg.querySelector('text, tspan') === null) {
    return;
  }
  const style = document.createElementNS(SVG_NS, 'style');
  style.textContent = fontCss;
  svg.insertBefore(style, svg.firstChild);
};

interface RasterizeSvgElementArgs {
  svg: SVGElement;
  width: number;
  height: number;
  fontCss?: string;
}

const rasterizeSvgElement = async ({
  svg,
  width,
  height,
  fontCss = '',
}: RasterizeSvgElementArgs): Promise<string | null> => {
  const clone = svg.cloneNode(true) as SVGElement;
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  const liveElements = [svg, ...Array.from(svg.querySelectorAll('*'))];
  const clonedElements = [clone, ...Array.from(clone.querySelectorAll('*'))];
  liveElements.forEach((liveEl, index) => {
    const clonedEl = clonedElements[index];
    if (clonedEl instanceof SVGElement) {
      applyComputedSvgPaints(liveEl, clonedEl);
      applyComputedSvgTypography(liveEl, clonedEl);
    }
  });
  embedFontCssInSvg(clone, fontCss);

  const serialized = new XMLSerializer().serializeToString(clone);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;

  try {
    const image = await loadImage(dataUrl);
    return drawImageToPng({ image, width, height });
  } catch {
    return null;
  }
};

export const hoistSvgForeignObjects = (root: HTMLElement) => {
  root.querySelectorAll('svg foreignObject').forEach(foreignObject => {
    if (!(foreignObject instanceof SVGForeignObjectElement)) {
      return;
    }

    const svg = foreignObject.closest('svg');
    const host = svg?.parentElement;
    if (!svg || !host) {
      foreignObject.remove();
      return;
    }

    const content = foreignObject.firstElementChild;
    const sourceRect = (content ?? foreignObject).getBoundingClientRect();
    if (!sourceRect.width && !sourceRect.height) {
      foreignObject.remove();
      return;
    }

    if (window.getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    const hostRect = host.getBoundingClientRect();
    const clone = document.createElement('div');
    clone.style.position = 'absolute';
    clone.style.left = `${sourceRect.left - hostRect.left}px`;
    clone.style.top = `${sourceRect.top - hostRect.top}px`;
    clone.style.width = `${Math.max(sourceRect.width, 1)}px`;
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '1';
    Array.from(foreignObject.childNodes).forEach(node => {
      clone.appendChild(node.cloneNode(true));
    });
    host.appendChild(clone);
    foreignObject.remove();
  });
};

export const replaceSvgsWithPngs = async (root: HTMLElement, fontCss = '') => {
  const svgs = Array.from(root.querySelectorAll('svg'));

  for (const svg of svgs) {
    const rect = svg.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width) || svg.clientWidth || 16, 1);
    const height = Math.max(
      Math.round(rect.height) || svg.clientHeight || 16,
      1
    );
    const png = await rasterizeSvgElement({ svg, width, height, fontCss });
    if (!png) {
      continue;
    }
    const image = createSizedImage({ src: png, width, height });
    const computed = window.getComputedStyle(svg);
    image.style.position = computed.position;
    image.style.top = computed.top;
    image.style.left = computed.left;
    image.style.right = computed.right;
    image.style.bottom = computed.bottom;
    image.className = svg.getAttribute('class') ?? '';
    svg.replaceWith(image);
  }
};
